'use client';

import { useEffect, useRef, useState } from 'react';
import { useFit } from '@/lib/useFit';

/**
 * The tab strip over an answer: alternatives of one result (Standard /
 * Software-AI / Easy, the three registers, the prompt styles), one open at a
 * time.
 *
 * ONE LINE, AT ANY WIDTH. The strip used to wrap: in a narrow pane the tabs ran
 * onto a second row and pushed Copy under them, and the underline that joins
 * the row to its pane cut through the middle of the block. Now the row
 * measures itself (lib/useFit) and the tabs that do not fit go behind a ▾ at
 * the end of it — the last tab first, the OPEN one never. Opening a tab from
 * the ▾ makes it the open one, so it takes a place in the row and another
 * steps behind the ▾ instead.
 *
 * The three strips were three copies of the same forty lines; this is the one
 * place they are drawn, so they cannot drift apart.
 */

export interface FitTab<K extends string> {
  key: K;
  label: string;
  title?: string;
  /** Being written right now — a pulsing dot, on the tab and on the ▾ if it is behind it. */
  loading?: boolean;
}

const TAB =
  'relative -mb-px flex items-center gap-1.5 rounded-t-lg px-3 py-1.5 text-[13px] font-medium transition ';
const TAB_ON = 'border-b-2 border-accent-600 text-accent-700 dark:text-accent-300';
const TAB_OFF =
  'border-b-2 border-transparent text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100';

function Dot() {
  return <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent-500" aria-hidden />;
}

export default function FitTabs<K extends string>({
  tabs,
  active,
  onPick,
  children,
}: {
  tabs: readonly FitTab<K>[];
  active: K;
  onPick: (key: K) => void;
  /** Sits at the far right of the row (Copy). */
  children?: React.ReactNode;
}) {
  // Last tab goes first; the open one is not on the list, so it never goes.
  const order = tabs.filter((t) => t.key !== active).map((t) => t.key).reverse();
  const { ref, dropped } = useFit<HTMLDivElement>(order, tabs.map((t) => t.label).join('|') + '|' + active, { refill: true });
  const behind = tabs.filter((t) => dropped.includes(t.key));

  const [open, setOpen] = useState(false);
  // Which way the menu hangs: towards whichever side of the window has the room.
  const [hang, setHang] = useState<'left' | 'right'>('left');
  const more = useRef<HTMLSpanElement>(null);
  const toggle = () => {
    const r = more.current?.getBoundingClientRect();
    if (r) setHang(r.left + r.width / 2 > window.innerWidth / 2 ? 'right' : 'left');
    setOpen((o) => !o);
  };
  useEffect(() => { if (!behind.length) setOpen(false); }, [behind.length]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!more.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div
      ref={ref}
      className="mb-3 flex items-center gap-1 border-b border-ink-200 dark:border-ink-700"
      role="tablist"
    >
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            aria-busy={t.loading}
            title={t.title}
            data-fit={t.key}
            onClick={() => onPick(t.key)}
            className={TAB + 'shrink-0 [[data-fit-tight]_&]:min-w-0 [[data-fit-tight]_&]:shrink ' + (on ? TAB_ON : TAB_OFF)}
          >
            <span className="truncate">{t.label}</span>
            {/* On the TAB, not only in the pane: clicking a second tab while the
                first is still being written leaves the pane showing the new
                one, and the dot is what says which tab the answer is coming
                for. */}
            {t.loading && <Dot />}
          </button>
        );
      })}

      {/* The tabs that did not fit. Drawn only when some did not (`data-fit-more`). */}
      <span ref={more} data-fit-more className="relative shrink-0">
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${behind.length} more`}
          title="More"
          className={TAB + TAB_OFF + ' px-2'}
        >
          <svg viewBox="0 0 20 20" className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
               fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {behind.some((t) => t.loading) && <Dot />}
        </button>
        {open && (
          <div
            role="menu"
            className={
              'absolute top-full z-30 mt-1 min-w-[10rem] max-w-[70vw] overflow-hidden rounded-xl border ' +
              'border-ink-200 bg-white py-1 shadow-lg dark:border-ink-700 dark:bg-ink-900 ' +
              (hang === 'right' ? 'right-0' : 'left-0')
            }
          >
            {behind.map((t) => (
              <button
                key={t.key}
                type="button"
                role="menuitem"
                title={t.title}
                onClick={() => { setOpen(false); onPick(t.key); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-medium text-ink-700
                           transition hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800"
              >
                <span className="min-w-0 flex-1 truncate">{t.label}</span>
                {t.loading && <Dot />}
              </button>
            ))}
          </div>
        )}
      </span>

      {children && <span className="ml-auto shrink-0 pb-1 pl-1">{children}</span>}
    </div>
  );
}
